var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Column, Icon, Page } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useEffect } from 'react';
import { useDeepCompareEffect, useToggle, useUpdateEffect } from 'react-use';
import { conversationsApi } from 'apis/conversations';
import { analytics } from 'app/utils/segment';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { ConversationsFiltersToolbar } from './components/filtersToolbar/conversationsFiltersToolbar';
import { ConversationsTable } from './components/table/conversationsTable';
import { conversationsActions } from './conversations.actions';
import styles from './conversations.view.module.scss';
import { conversationMatchesSearchParams } from './utils/conversationMatchesSearchParams/conversationMatchesSearchParams';
import { useConversations } from './utils/useConversations/useConversations';
import { useConversationsParamsToApiArg } from './utils/useConversationsParamsToApiArg/useConversationsParamsToApiArg';
/** Represents the conversations view component. */
export const ConversationsView = () => {
    const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);
    const dispatch = useAppDispatch();
    const { asyncStatus: conversationsAsyncStatus, conversations, hasNextPage, hasPreviousPage, nextPage, previousPage, } = useConversations();
    const { params } = useAppSearchParams('/conversations');
    const { paramsConversationsApiArg, paramsPaginatedApiArg, paramsSortedApiArg, } = useConversationsParamsToApiArg();
    const { asyncStatus: selectConversationsAsyncStatus, eligibleBulkConversationIds, isCurrentPageAllConversationsSelected, isCurrentPageSomeConversationsSelected, selectedConversationIds, totalFilteredConversations, clearSelectedConversations, selectAllCurrentPageConversation, selectAllEligibleConversations, deselectConversation, toggleSelectConversation, resetSelection, } = useSelectConversations({
        currentPageConversationIds: conversations.map(({ conversation: { id } }) => id),
        onFetchConversationIdsBulk: () => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield dispatch(conversationsApi.endpoints.readConversationsIdsBulk.initiate(paramsConversationsApiArg)).unwrap();
            return data;
        }),
    });
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const [hasCollapsedToolbars, toggleHasCollapseToolbars] = useToggle(!!isSmallScreen);
    useUpdateEffect(() => {
        toggleHasCollapseToolbars(!!isSmallScreen);
    }, [isSmallScreen]);
    // Handles re-setting the selection when conversation api arg related search
    // params change. This excludes the paginated api arg as these will change
    // during pagination and we want to preserve the selection when that occurs.
    // Note that we employ deep compare effect as we don't want to rely on
    // reference equality.
    useDeepCompareEffect(() => resetSelection(), [paramsConversationsApiArg]);
    // Handles conversations coming through the WebSocket. We do this to remove a
    // conversation from list of selected conversations if incoming conversation
    // no longer matches search params.
    useConversationEventsEffect(({ params: webSocketParams }) => {
        const { id } = webSocketParams.conversation;
        const isIncomingConversationSelected = selectedConversationIds.includes(id);
        const isIncomingConversationMatchesParams = conversationMatchesSearchParams(webSocketParams.conversation, params);
        if (!isIncomingConversationMatchesParams &&
            isIncomingConversationSelected) {
            deselectConversation(id);
        }
    });
    const showBulkToolbar = selectedConversationIds.length > 0;
    /** Handles selecting a row in the table. */
    const handleRowSelect = useCallback((rowId) => {
        toggleSelectConversation(rowId);
        analytics.conversationsSelectClicked({
            csid: rowId,
            selected: selectedConversationIds.includes(rowId),
        });
    }, [toggleSelectConversation, selectedConversationIds]);
    /** Handles selecting all current rows in the table. */
    const handleAllRowsSelect = useCallback(() => {
        selectAllCurrentPageConversation();
        analytics.conversationsSelectAllClicked();
    }, [selectAllCurrentPageConversation]);
    /** Handles navigating to the previous page. */
    const handlePreviousPage = useCallback(() => {
        previousPage();
        analytics.conversationsPreviousPageClicked();
    }, [previousPage]);
    /** Handles navigating to the next page. */
    const handleNextPage = useCallback(() => {
        nextPage();
        analytics.conversationsNextPageClicked();
    }, [nextPage]);
    // Handles setting the last search params.
    useEffect(() => {
        dispatch(conversationsActions.setLastSearchParams({
            apiArg: Object.assign(Object.assign(Object.assign({}, paramsConversationsApiArg), paramsPaginatedApiArg), paramsSortedApiArg),
        }));
    }, [
        dispatch,
        paramsConversationsApiArg,
        paramsPaginatedApiArg,
        paramsSortedApiArg,
    ]);
    const toolbar = (_jsxs("div", Object.assign({ className: classnames(styles.toolbars, {
            [styles.collapsed]: hasCollapsedToolbars,
        }) }, { children: [hasBulkOperations && (_jsx(ConversationsBulkToolbar, { className: classnames(styles.toolbar, styles.bulkActions, {
                    [styles.active]: showBulkToolbar,
                }), location: "conversations", asyncStatus: selectConversationsAsyncStatus, eligibleBulkConversationIds: eligibleBulkConversationIds, selectedConversationIds: selectedConversationIds, totalFilteredConversations: totalFilteredConversations, onClearAll: clearSelectedConversations, onSelectAll: selectAllEligibleConversations })), _jsx(ConversationsFiltersToolbar, { className: classnames(styles.toolbar, styles.filters, {
                    [styles.active]: !showBulkToolbar,
                }) })] })));
    const toggleToolbarButton = isSmallScreen ? (_jsx(Button, Object.assign({ variant: "tertiary", slotStart: _jsx(Icon, { name: hasCollapsedToolbars ? 'plus' : 'minus' }), onClick: toggleHasCollapseToolbars }, { children: hasCollapsedToolbars ? 'Show filters' : 'Hide filters' }))) : null;
    return (_jsxs(Page, { children: [_jsx(Page.Head, { title: "Conversations", controls: isSmallScreen ? toggleToolbarButton : toolbar }), isSmallScreen && (_jsx(Column.Row, Object.assign({ noVerticalPadding: true, noMinHeight: true }, { children: toolbar }))), _jsx(Page.Body, { children: _jsx(Page.Section, { children: _jsx(ConversationsTable, { items: conversations, asyncStatus: conversationsAsyncStatus, selectableRowsProps: {
                            selectedRowIds: selectedConversationIds,
                            allRowsSelected: conversationsAsyncStatus === 'fulfilled' &&
                                isCurrentPageAllConversationsSelected,
                            allRowsSelectedIndeterminate: conversationsAsyncStatus === 'fulfilled' &&
                                isCurrentPageSomeConversationsSelected,
                            onRowSelect: handleRowSelect,
                            onAllRowsSelect: handleAllRowsSelect,
                        }, paginationProps: conversationsAsyncStatus !== 'uninitialized' ||
                            conversations.length > 0
                            ? {
                                previousButtonDisabled: !hasPreviousPage ||
                                    conversationsAsyncStatus !== 'fulfilled',
                                previousButtonClick: handlePreviousPage,
                                nextButtonDisabled: !hasNextPage || conversationsAsyncStatus !== 'fulfilled',
                                nextButtonClick: handleNextPage,
                            }
                            : undefined }) }) })] }));
};
